import type { TypePolicy } from '@apollo/client';

export function isMBASubmission(status: unknown): status is `mba_${string}` {
  return typeof status === 'string' && status.startsWith('mba_');
}

// Accepted commands stay in audit data, not in the customer conversation.
// This does not infer delivery or a relationship to any provider output.
export function isAcceptedMBASubmission(message: {
  status?: string | null; isFromBuyer?: boolean | null;
} | null, isWhatsApp: boolean): boolean {
  return isWhatsApp && message?.isFromBuyer === false && message.status === 'mba_accepted';
}

export function mbaSubmissionLabel(status: unknown): string | null {
  if (!isMBASubmission(status)) return null;
  switch (status) {
    // Temporary steps are not shown (ITMV2-4422, 21 Oct); only problems are.
    case 'mba_queued': return null;
    case 'mba_submitting': return null;
    case 'mba_accepted': return null;
    case 'mba_rejected': return 'Not accepted';
    case 'mba_processing_failed': return 'MBA processing failed';
    case 'mba_skipped': return 'Skipped by MBA';
    default: return 'MBA outcome unknown';
  }
}

// Submission transitions are one-way. A delayed notification must not turn an
// accepted/failed command back into a pending send or a delivery receipt.
export function mergeSubmissionStatus<T>(existing: T, incoming: T): T {
  if (!isMBASubmission(existing)) return incoming;
  if (!isMBASubmission(incoming)) return existing;
  if (existing === incoming) return existing;
  if (['mba_rejected', 'mba_processing_failed', 'mba_skipped'].includes(existing)) return existing;
  if (existing === 'mba_accepted') {
    return ['mba_processing_failed', 'mba_skipped'].includes(incoming) ? incoming : existing;
  }
  if (existing !== 'mba_queued' && incoming === 'mba_queued') return existing;
  if (existing === 'mba_unknown' && incoming === 'mba_submitting') return existing;
  return incoming;
}

// History/refetch writes must obey the same monotonic rule as notifications.
export const chatMessageTypePolicy: TypePolicy = {
  fields: {
    status: {
      merge(existing: string | null | undefined, incoming: string | null | undefined) {
        return mergeSubmissionStatus(existing, incoming);
      },
    },
  },
};

const MBA_LABEL_SUFFIX = ' - Sent via MBA Message';
const SERVICE_LABEL_SUFFIX = ' - Sent via Service Message';

// Pilot sender labels (ITMV2-4422, 21 Oct): older MBA output carries the
// provider name, newer output the label itself.
export function displaySenderName(senderName: string | null | undefined, isWhatsApp: boolean): string | null | undefined {
  if (isWhatsApp && senderName === 'Meta Business Agent') return 'AI' + MBA_LABEL_SUFFIX;
  return senderName;
}

// Transport labels are not assignment transitions. MBA output and a human's
// submission can alternate while the assigned user remains unchanged.
export function assignmentSenderName(message: { isFromBuyer?: boolean | null; senderName?: string | null } | null,
  isWhatsApp: boolean): string | null {
  if (!message || message.isFromBuyer || !message.senderName) return null;
  if (isWhatsApp && ['WhatsApp', 'Meta Business Agent', 'via App'].includes(message.senderName)) return null;
  if (isWhatsApp && message.senderName.endsWith(MBA_LABEL_SUFFIX)) return null;
  if (isWhatsApp && message.senderName.endsWith(SERVICE_LABEL_SUFFIX)) {
    const agent = message.senderName.slice(0, -SERVICE_LABEL_SUFFIX.length);
    return agent && agent !== 'AI' ? agent : null;
  }
  if (isWhatsApp && message.senderName.startsWith('via App - ')) return message.senderName.slice('via App - '.length) || null;
  return message.senderName;
}
